// The single anatomy behind every state of the transaction-status popup:
//
//   rail → title → subtitle → children → detail panel → explorer link
//
// The four `ByStatus/*` bodies (in BOTH the `@common` and the `@web` copy) are
// thin wrappers over this, so a change here reaches the extension, mobile-web
// and native at once. See `docs/track-progress-redesign-plan.md`.
//
// Deliberately hook-free apart from theming: `openInTab` differs per build
// (`@common/utils/links` vs `@web/…/webapi/tab`) and the Retry dispatch in
// `Failed` differs too, which is exactly why those wrappers still exist twice.
// Nothing in here may reach for a controller.
import React, { FC, ReactNode, useCallback } from 'react'
import { Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

import StatusPanel, { StatusPanelRow } from './StatusPanel'
import StatusRail, { RailStep } from './StatusRail'

type Props = {
  /** Omit (or pass an empty array) to render no rail — e.g. the `Failed` alert. */
  steps?: RailStep[]
  title: string
  /**
   * Playwright binds to the title element: `txn-status` for the settled states,
   * `confirming-your` for in-progress. Never move these off the title, and
   * never reword the title strings — `gasTankPage.ts` compares one exactly.
   */
  titleTestID?: string
  subtitle?: string
  /** Rendered between the subtitle and the panel; keeps the legacy `children` slot alive. */
  children?: ReactNode
  /** Above the title — e.g. `SuccessAnimation` on the settled swap states. */
  topSlot?: ReactNode
  rows?: (StatusPanelRow | null | false | undefined)[]
  explorerLink?: string
  openExplorerText?: string
  /** Per-build tab opener. Required only when `explorerLink` is set. */
  openInTab?: (params: { url: string }) => Promise<any>
  beforeLinkOpenHandler?: Function
  /** Rendered before the link label (the `Refunded` bodies put `OpenIcon` there). */
  linkLeadingIcon?: ReactNode
  /** Rendered after the link label (`OpenIcon` on `@web`, `CopyText` on `@common`). */
  linkAccessory?: ReactNode
  /**
   * Keep the link row's height while there is no link yet, so the card does not
   * jump when the hash finally arrives. The `@common` `Completed` relied on this
   * during its `isLoading` state.
   */
  reserveLinkSpace?: boolean
  /**
   * Tighter vertical gaps, for a sheet that has to fit the 600px extension popup
   * (the DCA progress sheet has a 5-row panel plus two buttons under it).
   */
  compact?: boolean
}

const StatusBody: FC<Props> = ({
  steps,
  title,
  titleTestID = 'txn-status',
  subtitle,
  children,
  topSlot,
  rows,
  explorerLink,
  openExplorerText,
  openInTab,
  beforeLinkOpenHandler,
  linkLeadingIcon,
  linkAccessory,
  reserveLinkSpace,
  compact
}) => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { addToast } = useToast()

  const handleOpenExplorer = useCallback(async () => {
    if (!explorerLink || !openInTab) return

    if (beforeLinkOpenHandler) await beforeLinkOpenHandler()

    try {
      await openInTab({ url: explorerLink })
    } catch {
      addToast(t('Error opening explorer'), { type: 'error' })
    }
  }, [addToast, beforeLinkOpenHandler, explorerLink, openInTab, t])

  const hasLink = !!explorerLink && !!openExplorerText
  const showLinkRow = hasLink || reserveLinkSpace

  return (
    <View style={[flexbox.alignCenter, { width: '100%' }]}>
      {!!steps?.length && (
        <View style={{ width: '100%', marginBottom: compact ? 16 : 28 }}>
          <StatusRail steps={steps} />
        </View>
      )}

      {topSlot}

      <Text
        fontSize={22}
        weight="semiBold"
        color={theme.primaryText}
        style={[text.center, { marginBottom: subtitle ? 6 : 0 }]}
        testID={titleTestID}
      >
        {title}
      </Text>

      {!!subtitle && (
        <Text fontSize={16} weight="medium" appearance="secondaryText" style={text.center}>
          {subtitle}
        </Text>
      )}

      {children}

      {!!rows?.length && (
        <View style={{ width: '100%', marginTop: compact ? 12 : 20 }}>
          <StatusPanel rows={rows} />
        </View>
      )}

      {showLinkRow && (
        <Pressable
          onPress={handleOpenExplorer}
          disabled={!hasLink}
          style={[
            flexbox.directionRow,
            flexbox.alignCenter,
            flexbox.justifyCenter,
            { marginTop: compact ? 12 : 16 },
            !hasLink && { opacity: 0 }
          ]}
        >
          {linkLeadingIcon}
          <Text
            weight="medium"
            fontSize={13}
            appearance="primary"
            style={{
              textDecorationLine: 'underline',
              textDecorationColor: theme.primary,
              textDecorationStyle: 'solid'
            }}
          >
            {openExplorerText}
          </Text>
          {linkAccessory}
        </Pressable>
      )}
    </View>
  )
}

export default React.memo(StatusBody)
